import { useEffect, useRef, useCallback } from "react";
import { throttle } from "lodash";

/**
 * Hook personalizado para monitorar inatividade do usuário
 * e executar uma ação (logout) após um período de tempo sem atividade
 *
 * @param {Function} onTimeout - Função a ser executada quando o timeout expirar
 * @param {number} timeout - Tempo de inatividade em milissegundos (padrão: 30 minutos)
 * @param {boolean} enabled - Se o monitoramento de inatividade está ativo (padrão: true)
 */
const useInactivityTimeout = (
	onTimeout,
	timeout = 30 * 60 * 1000,
	enabled = true,
) => {
	const timeoutRef = useRef(null);

	// Limpa o timeout existente
	const clearTimer = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	// Reseta o timer de inatividade
	const resetTimer = useCallback(() => {
		clearTimer();

		timeoutRef.current = setTimeout(() => {
			if (onTimeout) {
				onTimeout();
			}
		}, timeout);
	}, [clearTimer, onTimeout, timeout]);

	// Handler de atividade do usuário com throttle para otimizar performance
	// Throttle de 1 segundo: só reseta o timer uma vez por segundo no máximo
	const handleActivity = useCallback(
		throttle(
			() => {
				resetTimer();
			},
			1000,
			{ leading: true, trailing: false },
		),
		[resetTimer],
	);

	useEffect(() => {
		// Quando desabilitado, garante que nenhum timer fique ativo
		if (!enabled) {
			clearTimer();
			return undefined;
		}

		// Eventos que indicam atividade do usuário
		const events = [
			"mousedown",
			"mousemove",
			"keypress",
			"keydown",
			"scroll",
			"touchstart",
			"click",
		];

		// Inicia o timer quando o componente é montado
		resetTimer();

		// Adiciona listeners para todos os eventos
		events.forEach((event) => {
			window.addEventListener(event, handleActivity, { passive: true });
		});

		// Cleanup: remove todos os listeners e limpa o timer
		return () => {
			events.forEach((event) => {
				window.removeEventListener(event, handleActivity);
			});
			clearTimer();
			// Cancela qualquer throttle pendente
			handleActivity.cancel();
		};
	}, [resetTimer, handleActivity, clearTimer, enabled]);

	return { resetTimer, clearTimer };
};

export default useInactivityTimeout;
